import type { FieldOf, SepaValue } from "@fobu/schema";
import { validIban } from "./iban";

type Sepa = FieldOf<"sepa">;

const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");
export const normalizeIban = (s: string) => s.replace(/\s+/g, "").toUpperCase();
export const groupIban = (s: string) => normalizeIban(s).replace(/(.{4})/g, "$1 ").trim();
/** „DE89 •••• •••• •••• •••• 00“ – für Mails an die Person selbst. */
export const maskIban = (s: string) => {
  const i = normalizeIban(s);
  return i.length > 8 ? groupIban(i.slice(0, 4) + "•".repeat(i.length - 6) + i.slice(-2)) : i;
};
const BIC = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/;

export function sepaValue(value: unknown): SepaValue | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const v = value as Record<string, unknown>;
  const out: SepaValue = { holder: str(v.holder), iban: normalizeIban(str(v.iban)), accepted: v.accepted === true };
  if (str(v.bic)) out.bic = str(v.bic).toUpperCase().replace(/\s+/g, "");
  if (str(v.reference)) out.reference = str(v.reference);
  if (str(v.date)) out.date = str(v.date);
  return out;
}

export function validateSepa(field: Sepa, value: unknown): { code: "invalid_type" | "incomplete_sepa" | "invalid_iban" | "invalid_bic" | "consent_required"; message: string } | null {
  const v = sepaValue(value);
  if (!v) return { code: "invalid_type", message: "Ungültige Angaben." };
  if (!v.holder) return { code: "incomplete_sepa", message: "Bitte den Namen der Kontoinhaberin oder des Kontoinhabers angeben." };
  if (!v.iban) return { code: "incomplete_sepa", message: "Bitte die IBAN angeben." };
  if (!validIban(v.iban)) return { code: "invalid_iban", message: "Die IBAN ist nicht gültig – bitte prüfen." };
  if (v.bic && !BIC.test(v.bic)) return { code: "invalid_bic", message: "Die BIC ist nicht gültig." };
  if (field.askBic && !v.bic && !/^(DE|AT|CH|LI|LU|NL|BE|FR|IT|ES|PT|FI|IE|EE|LV|LT|SK|SI|MT|CY|GR)/.test(v.iban))
    return { code: "invalid_bic", message: "Für Konten außerhalb des Euro-Raums bitte die BIC angeben." };
  if (!v.accepted) return { code: "consent_required", message: "Bitte erteile das Lastschriftmandat." };
  return null;
}

export function formatSepa(value: unknown, opts: { mask?: boolean } = {}): string {
  const v = sepaValue(value);
  if (!v?.iban) return "";
  const parts = [v.holder, opts.mask ? maskIban(v.iban) : groupIban(v.iban), v.bic ? `BIC ${v.bic}` : "", v.reference ? `Mandatsreferenz ${v.reference}` : ""];
  const date = v.date ? `erteilt am ${v.date.split("-").reverse().join(".")}` : "";
  return [...parts, date].filter(Boolean).join(", ");
}

